import { AsyncPipe, DatePipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { MatButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { ActivatedRoute, RouterLink } from '@angular/router'
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core'
import { distinctUntilChanged, map, switchMap } from 'rxjs'
import { MeetingsService, RetentionExtensionRequestReview } from '../meetings.service'
import { SpinnerComponent } from '../spinner/spinner.component'
import { DATE_TIME_FORMAT, isEqual, resolveLocalTimeZone } from '../utils'

/**
 * Where the email asking an administrator to approve a retention extension lands. Kept to one
 * narrow column so that it reads as well on the phone the email is likely opened on.
 */
@Component({
  selector: 'app-retention-extension-request-page',
  standalone: true,
  imports: [AsyncPipe, DatePipe, MatButton, MatIcon, RouterLink, TranslatePipe, TranslateDirective, SpinnerComponent],
  templateUrl: './retention-extension-request-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './retention-extension-request-page.component.scss',
})
export class RetentionExtensionRequestPageComponent {
  private route = inject(ActivatedRoute)
  private meetingsService = inject(MeetingsService)

  // Off the parameters rather than a snapshot: moving from one request's link to another's reuses the
  // page, which would otherwise keep showing, and answering, the first one.
  request$ = this.route.paramMap.pipe(
    map((params) => ({ meetingId: Number(params.get('id')), requestId: Number(params.get('requestId')) })),
    distinctUntilChanged(isEqual),
    switchMap(({ meetingId, requestId }) => this.meetingsService.getRetentionExtensionRequest(meetingId, requestId))
  )
  decideMutation = this.meetingsService.decideRetentionExtensionRequestMutation()
  dateTimeFormat = DATE_TIME_FORMAT
  localTimeZone = resolveLocalTimeZone()
  isDeciding = false

  daysKey(request: RetentionExtensionRequestReview): string {
    return request.extensionInDays === 1 ? 'MEETINGS.RETENTION_EXTENSION_REQUEST.DAY' : 'MEETINGS.RETENTION_EXTENSION_REQUEST.DAYS'
  }

  async decide(request: RetentionExtensionRequestReview, decision: 'approve' | 'decline') {
    this.isDeciding = true
    try {
      await this.decideMutation.mutateAsync({ meetingId: request.meetingId, requestId: request.id, decision })
    } catch {
      // The toast has said so; the buttons stay for another try.
    } finally {
      this.isDeciding = false
    }
  }
}
